import { useState, useEffect, useContext } from 'react';
import {
    Backdrop,
    Box,
    TextField,
    Stack,
    InputLabel,
    MenuItem,
    FormControl,
    Select,
    Typography
} from '@mui/material';

import CustomButton from "../custom_components/CustomButton";
import CustomModalHeader from "../custom_components/CustomModalHeader";

import { handleNumberInput } from '../utils';
import { UserContext } from '../../context/UserContext';

const EditCreatedOrder = ({ open, onClose, orderInfo, onUpdate }) => {

    const { categories } = useContext(UserContext);

    // Order input fields
    const [itemName, setItemName] = useState("");
    const [quantity, setQuantity] = useState(1);
    const [category, setCategory] = useState("");
    const [price, setPrice] = useState("");
    const [sfee, setSF] = useState("");
    const [cargo, setCargo] = useState("");

    useEffect(() => {
        if (!open || !orderInfo) return;

        setItemName(orderInfo.itemName ?? "");
        setQuantity(orderInfo.quantity ?? "");
        setCategory(orderInfo.category ?? "");
        setPrice(orderInfo.price ?? "");
        setSF(orderInfo.sfee ?? "");
        setCargo(orderInfo.cargo ?? "");

    }, [open, orderInfo]);

    // SAVE EDIT
    const handleSaveEdit = async () => {
        await onUpdate({
            orderId: orderInfo.orderId,
            itemName: itemName,
            quantity: quantity,
            category: category,
            price: price,
            sfee: sfee,
            cargo: cargo,
            total: (Number(quantity) * Number(price)) + Number(sfee),
        });

        onClose();
    };

    // SET CARGO
    const handleChangeCargo = (value) => {
        setCargo(value);
        if (value === "SC") {
            setSF("200");
        } else if (value === "AC") {
            setSF("400");
        }
    };

    return (
        <Backdrop
            aria-hidden="false"
            open={open}
            sx={{
                zIndex: (theme) => theme.zIndex.drawer + 1,
                backdropFilter: "blur(1px)",
            }}
        >
            <Box
                sx={{
                    width: 580,
                    backgroundColor: "#ffffff",
                    borderRadius: 2,
                    p: 2.5,
                    boxShadow: 6,

                    position: "relative",
                    overflow: "hidden",
                    "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "4px",
                        background: "#2e3489"
                    }
                }}
            >

                {/* HEADER */}
                <CustomModalHeader
                    title="Edit Order"
                    subtitle={`ID: ${orderInfo.orderId || ""}`}
                    onClose={onClose}
                />

                {/* FORM */}
                <Stack direction="column" sx={{ gap: 2 }}>
                    <TextField
                        label="Item Name"
                        size="small"
                        autoComplete="off"
                        value={itemName}
                        onChange={(e) => setItemName(e.target.value)}
                    />

                    <Stack flexDirection="row" gap={1}>
                        <TextField
                            fullWidth
                            label="Quantity"
                            size="small"
                            type="number"
                            autoComplete="off"
                            value={quantity}
                            onChange={(e) => {
                                const value = Number(e.target.value);
                                const stock = Number(orderInfo.stock || 0);

                                if (value < 1) {
                                    setQuantity(1);
                                    return;
                                }
                                if (orderInfo.inventory) {
                                    if (value > stock) {
                                        setQuantity(stock);
                                        return;
                                    }
                                }
                                setQuantity(value);
                            }}
                        />
                        <FormControl fullWidth size="small">
                            <InputLabel id="demo-select-small-label">
                                Category
                            </InputLabel>
                            <Select
                                labelId="demo-select-small-label"
                                id="demo-select-small"
                                defaultValue="Watch"
                                value={category}
                                label="Category"
                                onChange={(e) => setCategory(e.target.value)}
                            >
                                {categories.map((category, index) => (
                                    <MenuItem key={index} value={category.ItemCategory}>
                                        {category.ItemCategory}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Stack>

                    <Stack flexDirection="row" gap={1}>
                        <TextField
                            label="Price"
                            size="small"
                            autoComplete="off"
                            fullWidth
                            value={price}
                            onChange={(e) => handleNumberInput(e, setPrice)}
                        />
                        <FormControl fullWidth size="small">
                            <InputLabel id="demo-select-small-label">
                                Cargo Type / SC, AC
                            </InputLabel>
                            <Select
                                labelId="demo-select-small-label"
                                id="demo-select-small"
                                value={cargo}
                                label="Cargo Type / SC, AC"
                                onChange={(e) => handleChangeCargo(e.target.value)}
                            >
                                <MenuItem value="SC">Sea Cargo</MenuItem>
                                <MenuItem value="AC">Air Cargo</MenuItem>
                                <MenuItem value="PH">PH</MenuItem>
                            </Select>
                        </FormControl>
                    </Stack>

                    <Stack flexDirection="row" gap={1} alignItems="center">
                        <TextField
                            label="Shipping Fee"
                            size="small"
                            autoComplete="off"
                            sx={{ width: 186 }}
                            value={sfee}
                            onChange={(e) => handleNumberInput(e, setSF)}
                        />

                        {orderInfo.inventory && (
                            <Typography
                                sx={{
                                    color:
                                        Math.max(0, Number(orderInfo.stock || 0)
                                            - Number(quantity || 0)) === 0
                                            ? "#d32f2f"
                                            : "#2e7d32",
                                    fontWeight: 'bold'
                                }}
                            >
                                Stock: {Math.max(0, Number(orderInfo.stock || 0)
                                    - Number(quantity || 0))}
                            </Typography>
                        )}
                    </Stack>

                </Stack>

                {/* ACTIONS */}
                <Stack
                    direction="row"
                    justifyContent="flex-end"
                    spacing={2}
                    mt={4}
                >

                    <CustomButton
                        label="Cancel"
                        variant="outlined"
                        hoverColor="#d14249"
                        onClick={onClose}
                    />

                    <CustomButton
                        label="Save Changes"
                        onClick={handleSaveEdit}
                    />

                </Stack>

            </Box>

        </Backdrop >
    );
};

export default EditCreatedOrder;
